import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

/** "just now" / "5 min ago" / "3 hr ago" / "2 days ago"; "—" when empty. */
export const relativeTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const then = dayjs(iso);
  if (!then.isValid()) return "—";
  const seconds = dayjs().diff(then, "second");
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
};

export const isDateOnlyString = (value: unknown): value is string => {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
};

export const parseInTimezone = (
  value: string | Date | null | undefined,
  tz: string
): dayjs.Dayjs | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return dayjs(value).tz(tz);
  }

  if (isDateOnlyString(value)) {
    const parsed = dayjs.tz(value, "YYYY-MM-DD", tz);
    return parsed.isValid() ? parsed : null;
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.tz(tz) : null;
};

export const formatInTimezone = (
  value: string | Date | null | undefined,
  tz: string,
  format: string
): string => {
  const parsed = parseInTimezone(value, tz);
  if (!parsed) {
    return "";
  }
  return parsed.format(format);
};

/**
 * Re-key weather entries from the weather source date onto a target date,
 * preserving the hour-of-day for each entry. This lets us pull weather
 * conditions from one calendar day and apply them to a different calendar
 * day (the project / landscape date).
 */
export const transposeWeatherTimestamps = (
  weather: Record<string, any> | undefined,
  weatherDate: dayjs.Dayjs,
  projectDate: dayjs.Dayjs,
): Record<string, any> | undefined => {
  if (!weather) return undefined;

  const dayOffset = dayOffsetBetween(weatherDate, projectDate);
  if (dayOffset === 0) return weather;

  const transposed: Record<string, any> = {};
  for (const [key, value] of Object.entries(weather)) {
    transposed[shiftTimestampByDays(key, dayOffset)] = value;
  }
  return transposed;
};

const dayOffsetBetween = (weatherDate: dayjs.Dayjs, projectDate: dayjs.Dayjs): number =>
  projectDate.startOf("day").diff(weatherDate.startOf("day"), "day");

const shiftTimestampByDays = (key: string, dayOffset: number): string =>
  dayjs(key).add(dayOffset, "day").toISOString();

/**
 * Same transposition as transposeWeatherTimestamps, applied to an ordered
 * timestamp list (the weather grid's `timestamps`). Keeps the grid's
 * timestamps string-equal to the transposed `weather` keys so the backend's
 * "grid timestamps == weather keys" validation holds.
 */
export const transposeTimestampList = (
  timestamps: string[],
  weatherDate: dayjs.Dayjs,
  projectDate: dayjs.Dayjs,
): string[] => {
  const dayOffset = dayOffsetBetween(weatherDate, projectDate);
  if (dayOffset === 0) return timestamps;
  return timestamps.map((t) => shiftTimestampByDays(t, dayOffset));
};

export const toApiDate = (value: string | Date | null | undefined, tz: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  if (typeof value === "string" && isDateOnlyString(value)) {
    return value;
  }

  const parsed = parseInTimezone(value, tz);
  if (!parsed) {
    return undefined;
  }

  return parsed.format("YYYY-MM-DD");
};
